"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.defaultTrialReactivationConfig = exports.experimentVariantRatio = exports.experimentVariantOn = exports.experimentVariantOff = exports.ExperimentConfiguration = exports.ExperimentConfigurationKind = exports.ExperimentVariant = exports.defaultKnowledgeBaseQueryConfig = exports.defaultStreamingBufferConfig = exports.defaultLibraryToolSearchConfig = exports.searchClientForCopilotPartLibraryVariants = exports.searchClientForPartLibraryVariants = exports.searchClientVariants = void 0;
exports.getLibraryToolSearchConfig = getLibraryToolSearchConfig;
exports.getStreamingBufferConfig = getStreamingBufferConfig;
exports.getKnowledgeBaseQueryConfig = getKnowledgeBaseQueryConfig;
exports.getExperimentVariant = getExperimentVariant;
exports.isExperimentActive = isExperimentActive;
exports.getTrialReactivationConfig = getTrialReactivationConfig;
const tslib_1 = require("tslib");
const zod_1 = tslib_1.__importDefault(require("zod"));
/**
 * @see https://app.launchdarkly.com/projects/flux-app/flags/search-client/targeting?env=production&selected-env=production
 */
exports.searchClientVariants = ["on", "off", "typesense", "algolia"];
const SearchClientEnum = zod_1.default.enum(exports.searchClientVariants);
exports.searchClientForPartLibraryVariants = ["on", "off", "typesense", "algolia", "qdrant"];
const SearchClientForPartLibraryEnum = zod_1.default.enum(exports.searchClientForPartLibraryVariants);
/**
 * @deprecated This flag is scheduled for deprecation. The default is now "hybrid" and all call sites
 * should assume hybrid search. Remove this flag and related branching once fully rolled out.
 */
exports.searchClientForCopilotPartLibraryVariants = ["algolia", "qdrant", "hybrid"];
const SearchClientForCopilotPartLibraryEnum = zod_1.default.enum(exports.searchClientForCopilotPartLibraryVariants);
const LibraryToolSearchConfigSchema = zod_1.default.object({
    hybridPageSize: zod_1.default.number().int().min(1).default(25).catch(25),
    qdrantPageSize: zod_1.default.number().int().min(1).default(25).catch(25),
    maxSearches: zod_1.default.number().int().min(1).default(20).catch(20),
    maxRecursions: zod_1.default.number().int().min(1).default(50).catch(50),
    agenticRawResponses: zod_1.default.boolean().default(false),
    facetFiltersEnabled: zod_1.default.boolean().default(false),
    llmModel: zod_1.default.string().default("gpt-5-mini").catch("gpt-5-mini"),
});
exports.defaultLibraryToolSearchConfig = {
    hybridPageSize: 25,
    qdrantPageSize: 25,
    maxSearches: 20,
    maxRecursions: 50,
    agenticRawResponses: false,
    facetFiltersEnabled: false,
    llmModel: "gpt-5-mini",
};
function getLibraryToolSearchConfig(configOrUndefined) {
    if (!configOrUndefined) {
        return exports.defaultLibraryToolSearchConfig;
    }
    return LibraryToolSearchConfigSchema.catch(exports.defaultLibraryToolSearchConfig).parse(configOrUndefined);
}
const StreamingBufferConfigSchema = zod_1.default.object({
    chunkSize: zod_1.default.number().int().min(1).default(5).catch(5),
    flushInterval: zod_1.default.number().int().min(100).default(1000).catch(1000),
    holdIncompleteUrls: zod_1.default.boolean().default(true),
    enabled: zod_1.default.boolean().default(true),
});
exports.defaultStreamingBufferConfig = {
    chunkSize: 5,
    flushInterval: 1000,
    holdIncompleteUrls: true,
    enabled: true,
};
function getStreamingBufferConfig(configOrUndefined) {
    if (!configOrUndefined) {
        return exports.defaultStreamingBufferConfig;
    }
    return StreamingBufferConfigSchema.catch(exports.defaultStreamingBufferConfig).parse(configOrUndefined);
}
const KnowledgeBaseQueryConfigSchema = zod_1.default.object({
    systemKnowledgeMinSimilarity: zod_1.default.number().min(0).max(1).default(0.56).catch(0.56),
    systemResultsLimit: zod_1.default.number().int().min(1).default(40).catch(40),
    userKnowledgeMinSimilarity: zod_1.default.number().min(0).max(1).default(0.3).catch(0.3),
    userResultsLimit: zod_1.default.number().int().min(1).default(10).catch(10),
});
exports.defaultKnowledgeBaseQueryConfig = {
    systemKnowledgeMinSimilarity: 0.56,
    systemResultsLimit: 40,
    userKnowledgeMinSimilarity: 0.3,
    userResultsLimit: 10,
};
function getKnowledgeBaseQueryConfig(configOrUndefined) {
    if (!configOrUndefined) {
        return exports.defaultKnowledgeBaseQueryConfig;
    }
    return KnowledgeBaseQueryConfigSchema.catch(exports.defaultKnowledgeBaseQueryConfig).parse(configOrUndefined);
}
/**
 * The flag has four values, `off`/`control`, and `on`/`test`. Both on/test get the feature; off/control do not.
 *
 * The reason there are four states is so that we can enable and disable the flag for anybody we want, keeping them out
 * of experiment results, while also allowing PMs to have full control of the experiment groups ("test" and "control")
 *
 * This type is independent of the feature name, so many features can use it.
 */
exports.ExperimentVariant = zod_1.default.enum(["on", "off", "control", "test"]);
function getExperimentVariant(variantOrUndefined, defaultIfInvalid = exports.ExperimentVariant.enum.off) {
    return exports.ExperimentVariant.catch(defaultIfInvalid).parse(variantOrUndefined);
}
/**
 * Whether, given a variant (like "test" or "control" or "on" or "off"), the user should receive the feature.
 *
 * Includes a mechanism for specifying the default if the variant is undefined or malformed ( e.g. this happens before
 * LaunchDarkly has finished loading)
 */
function isExperimentActive(variantOrUndefined, defaultIfInvalid = exports.ExperimentVariant.enum.off) {
    const variant = getExperimentVariant(variantOrUndefined, defaultIfInvalid);
    return variant === exports.ExperimentVariant.enum.on || variant === exports.ExperimentVariant.enum.test;
}
/**
 * This flag has different values that still resolve the eventual feature to an ExperimentVariant
 *
 * This is used in cases where we cannot configure the experiment variant directly in LaunchDarkly, because of timing
 * limitation around when the user is loaded.
 */
exports.ExperimentConfigurationKind = zod_1.default.enum(["on", "off", "ratio"]);
exports.ExperimentConfiguration = zod_1.default.discriminatedUnion("kind", [
    zod_1.default.object({ kind: zod_1.default.literal(exports.ExperimentConfigurationKind.enum.on) }),
    zod_1.default.object({ kind: zod_1.default.literal(exports.ExperimentConfigurationKind.enum.off) }),
    zod_1.default.object({
        kind: zod_1.default.literal(exports.ExperimentConfigurationKind.enum.ratio),
        ratio: zod_1.default.number().min(0).max(1),
    }),
]);
exports.experimentVariantOff = { kind: exports.ExperimentConfigurationKind.enum.off };
exports.experimentVariantOn = { kind: exports.ExperimentConfigurationKind.enum.on };
const experimentVariantRatio = (ratio) => ({
    kind: exports.ExperimentConfigurationKind.enum.ratio,
    ratio,
});
exports.experimentVariantRatio = experimentVariantRatio;
/**
 * Configuration for trial reactivation eligibility checks
 *
 * This is used as a feature flag value to control whether trial reactivation is enabled,
 * the watershed date, and minimum expired days for trial reactivation eligibility.
 *
 * Three valid configurations:
 * 1. Empty object `{}` - No override, use constants from packages/plans/src/config/trial.ts
 * 2. `{ enabled: false }` - Disabled, no other fields required
 * 3. `{ enabled: true, watershedDate: "YYYY-MM-DD", minimumExpiredDays: 30 }` - Enabled with explicit values
 */
const TrialReactivationConfigDisabledSchema = zod_1.default
    .object({
    enabled: zod_1.default.literal(false),
})
    .strict();
const TrialReactivationConfigEnabledSchema = zod_1.default
    .object({
    enabled: zod_1.default.literal(true),
    /**
     * ISO 8601 date string (YYYY-MM-DD format) for the watershed date
     * Trials that ended after this date are not eligible for reactivation
     * Uses Zod's built-in date() validator to ensure proper format
     */
    watershedDate: zod_1.default.string().date(),
    /**
     * Number of days a trial must have been expired before it can be reactivated
     */
    minimumExpiredDays: zod_1.default.number().int().min(0),
})
    .strict();
const TrialReactivationConfigDefaultSchema = zod_1.default.object({}).strict();
const TrialReactivationConfigSchema = zod_1.default.union([
    TrialReactivationConfigDisabledSchema,
    TrialReactivationConfigEnabledSchema,
    TrialReactivationConfigDefaultSchema,
]);
exports.defaultTrialReactivationConfig = {};
function getTrialReactivationConfig(configOrUndefined) {
    if (configOrUndefined === undefined || configOrUndefined === null) {
        return exports.defaultTrialReactivationConfig;
    }
    return TrialReactivationConfigSchema.catch(exports.defaultTrialReactivationConfig).parse(configOrUndefined);
}
//# sourceMappingURL=featureFlags.js.map